import { useEffect } from 'react';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useSnapshot } from 'valtio';

import {
  applyFaceExpression,
  applyShowSkeleton,
  applySkinHex,
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';

import { characterPickerStore, sceneSettingsStore } from 'stores';

// This hook mimics useLoadCharacter, but without loading the character mesh
export default function useUpdateCharacter(
  meshData: GLTF,
  forceSkeleton?: boolean,
) {
  const {
    characterSettings: { skinHex, faceExpression, charUploadID, showSkeleton },
  } = useSnapshot(sceneSettingsStore.state);

  const { activeCharacter } = useSnapshot(characterPickerStore.state);

  // Ensure the active character ID in both stores is synchronized
  useEffect(() => {
    if (charUploadID !== characterPickerStore.state.activeCharacterID) {
      characterPickerStore.setActiveCharacterID(charUploadID);
    }
  }, [charUploadID]);

  const character = activeCharacter;
  const characterID = character?.PK ?? charUploadID;
  const rotationOffsets = character?.rotationOffsets;

  useEffect(() => {
    if (meshData) {
      hideMeshMaterials(meshData);
      turnOnShadows(meshData);
      disableFrustumCulled(meshData);
    }
  }, [meshData]);

  useEffect(() => {
    if (meshData) {
      applySkinHex(skinHex, characterID)(meshData);
    }
  }, [characterID, meshData, skinHex]);

  useEffect(() => {
    if (meshData) {
      applyFaceExpression(faceExpression)(meshData);
    }
  }, [faceExpression, meshData]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, forceSkeleton || showSkeleton);
    }
  }, [forceSkeleton, showSkeleton, meshData]);

  return { meshData, rotationOffsets };
}
